import chatChannelProvider from '../../chats/chatChannelProvider';
import { AppThunkAction } from '../index';
import { ChatConversationId, ChatMessage, ChatMessagePayloadType, ChatMessagesPage } from '../../types/chatMessage';
import { ChatEvent } from '../../types/chatEvent';
import {
  selectChatMessageConversationById,
  selectCurrentChatMessageConversation,
} from '../../reducers/chats/messages';
import { createChatChannelsAction } from './index';
import { selectChatClientByConversationId } from '../../reducers/chats/clients';
import { selectActiveChatChannel } from '../../reducers/chats';
import { recordChatClientActivity } from './clients';
import dateTimeService from '../../service/dateTimeService';
import { markMessageAsRead, markMessageAsUnread } from './unreadMessages';
import { inboxAnalytics } from 'src/service/analytics/inbox.analytics';
import { resolveMessageForCurrentConversation } from './conversationIdentity';

const upsertOneChatMessageAction = createChatChannelsAction<{ message: ChatMessage }>('messages/upsertOne');
const updateOneChatMessageAction = createChatChannelsAction<{ message: ChatMessage }>('messages/updateOne');

const chatMessagesFetchPendingAction = createChatChannelsAction<{
  conversationId: ChatConversationId;
  refreshInBackground: boolean;
}>('messages/fetchPending');

const chatMessagesFetchSuccessAction = createChatChannelsAction<{
  conversationId: ChatConversationId;
  messagesPage: ChatMessagesPage;
}>('messages/fetchSuccess');

const chatMessagesFetchFailureAction = createChatChannelsAction<{
  conversationId: ChatConversationId;
  errorMessage: string;
}>('messages/fetchFailure');

type FetchChatMessagesParams = {
  readonly page: number;
  readonly size: number;
  readonly refreshInBackground?: boolean;
};

const conversationIdKey = (id: ChatConversationId) =>
  `${id.channel}|${id.senderId}|${id.clientId}`;

/**
 * Guards against duplicate identical message fetches firing in parallel.
 * Opening a conversation triggers a fetch both from `selectClient` and reactively
 * from the `useMessages` effect; without this guard the same request hits the API twice.
 */
const inFlightMessageFetches = new Set<string>();

const fetchChatMessages = (conversationId: ChatConversationId, params: FetchChatMessagesParams): AppThunkAction => {
  return async (dispatch, getState) => {
    const { channel } = conversationId;
    const { page, size, refreshInBackground = false } = params;

    const client = selectChatClientByConversationId(getState(), conversationId);

    if (!client) {
      return;
    }

    const fetchKey = `${conversationIdKey(conversationId)}|${page}|${size}|${refreshInBackground}`;
    if (inFlightMessageFetches.has(fetchKey)) {
      return;
    }
    inFlightMessageFetches.add(fetchKey);

    dispatch(chatMessagesFetchPendingAction(channel, { conversationId, refreshInBackground }));
    try {
      const messagesPage: ChatMessagesPage = await chatChannelProvider.get(channel)
        .api.fetchMessages(client, { page, size });

      dispatch(chatMessagesFetchSuccessAction(channel, { conversationId, messagesPage }));
    } catch {
      dispatch(chatMessagesFetchFailureAction(channel, {
        conversationId,
        errorMessage: 'Failed to fetch message conversation',
      }));
    } finally {
      inFlightMessageFetches.delete(fetchKey);
    }
  }
};

const MESSAGES_CHUNK_SIZE: number = 20;
const MEDIA_PAYLOAD_TYPES: ChatMessagePayloadType[] = [
  ChatMessagePayloadType.IMAGE,
  ChatMessagePayloadType.VIDEO,
  ChatMessagePayloadType.DOC,
  ChatMessagePayloadType.IMAGE_BUTTON,
];

const hasPartialMediaPayload = (message: ChatMessage): boolean => {
  return MEDIA_PAYLOAD_TYPES.includes(message.payloadType) && !message.attachments?.length;
};

export const fetchFirstChatConversationChunk = (conversationId: ChatConversationId): AppThunkAction => {
  return async (dispatch) => {
    dispatch(fetchChatMessages(conversationId, {
      page: 0,
      size: MESSAGES_CHUNK_SIZE,
    }));
  };
};

export const fetchNextChatConversationChunk = (conversationId: ChatConversationId): AppThunkAction => {
  return async (dispatch, getState) => {
    const conversation = selectChatMessageConversationById(getState(), conversationId);

    if (conversation && conversation.totalMessages > conversation.numberOfMessages) {
      dispatch(fetchChatMessages(conversationId, {
        page: 0,
        size: conversation.numberOfMessages + MESSAGES_CHUNK_SIZE,
      }));
    }
  };
};

export const refreshChatConversationInBackground = (conversationId: ChatConversationId): AppThunkAction => {
  return async (dispatch, getState) => {
    const conversation = selectChatMessageConversationById(getState(), conversationId);

    if (conversation) {
      dispatch(fetchChatMessages(conversationId, {
        page: 0,
        size: conversation.numberOfMessages,
        refreshInBackground: true,
      }));
    }
  };
};

const REFRESH_DEBOUNCE_MS = 400;
const refreshDebounceTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

/** Debounced refresh: multiple incoming media in quick succession trigger a single refetch. */
export const refreshChatConversationInBackgroundDebounced = (conversationId: ChatConversationId): AppThunkAction => {
  return (dispatch, getState) => {
    const key = conversationIdKey(conversationId);
    const existing = refreshDebounceTimeouts.get(key);
    if (existing) clearTimeout(existing);

    refreshDebounceTimeouts.set(
      key,
      setTimeout(() => {
        refreshDebounceTimeouts.delete(key);
        dispatch(refreshChatConversationInBackground(conversationId));
      }, REFRESH_DEBOUNCE_MS)
    );
  };
};

export const addOutgoingChatMessage = (message: ChatMessage) => (
  upsertOneChatMessageAction(message.channel, { message })
);

export const handleChatEvent = (chatEvent: ChatEvent): AppThunkAction => {
  return (dispatch, getState) => {
    const classification = inboxAnalytics.classifyEvent(chatEvent.status);
    if (classification === 'sent') {
      inboxAnalytics.messageSendConfirmed(chatEvent);
    } else if (classification === 'delivered') {
      inboxAnalytics.messageDelivered(chatEvent);
    } else if (classification === 'failed') {
      inboxAnalytics.messageFailed(chatEvent);
    }

    const state = getState();
    const activeChannel = selectActiveChatChannel(state);

    if (activeChannel === chatEvent.channel) {
      const conversation = selectCurrentChatMessageConversation(state, activeChannel);
      let existingChatMessage = conversation?.entities
        .find(msg => msg.msgId === chatEvent.msgId);

      // Fallback: event can arrive for a message that is not in currently opened conversation
      if (!existingChatMessage) {
        const channelConversations = state.chatChannels?.[activeChannel]?.messages || [];
        existingChatMessage = channelConversations
          .flatMap((it: { entities?: ChatMessage[] }) => it.entities || [])
          .find((msg: ChatMessage) => msg.msgId === chatEvent.msgId);
      }

      if (existingChatMessage) {
        const updatedChatMessage: ChatMessage = {
          ...existingChatMessage,
          status: chatEvent.status,
          statusDetails: chatEvent.statusDetails,
        };

        dispatch(updateOneChatMessageAction(updatedChatMessage.channel, { message: updatedChatMessage }));
      }
    }
  };
}

export const handleChatIncomingMessage = (chatMessage: ChatMessage): AppThunkAction => {
  return (dispatch, getState) => {
    inboxAnalytics.messageReceived(chatMessage);

    const state = getState();
    const { normalizedMessage, isOpenConversationByUser } = resolveMessageForCurrentConversation(state, chatMessage);
    const shouldHydrateMedia = hasPartialMediaPayload(normalizedMessage);

    if (isOpenConversationByUser) {
      dispatch(upsertOneChatMessageAction(normalizedMessage.channel, { message: normalizedMessage }));
      // The message is visible to the user right now, so acknowledge it immediately
      // to prevent delayed unread counters from backend sync.
      dispatch(markMessageAsRead(normalizedMessage, true));

      // WS payloads for media can be partial (without attachment URL).
      // Sync active conversation from API to hydrate media content deterministically.
      if (shouldHydrateMedia) {
        const conversationId: ChatConversationId = {
          channel: normalizedMessage.channel,
          senderId: normalizedMessage.senderId,
          clientId: normalizedMessage.clientId,
        };
        dispatch(refreshChatConversationInBackgroundDebounced(conversationId));
      }
    } else {
      dispatch(markMessageAsUnread(normalizedMessage));
    }

    dispatch(recordChatClientActivity({
      channel: normalizedMessage.channel,
      clientId: normalizedMessage.clientId,
      senderId: normalizedMessage.senderId,
      activityTime: dateTimeService.getCurrentTimestamp(),
    }));
  };
};

export const handleChatOutgoingMessage = (chatMessage: ChatMessage): AppThunkAction => {
  return (dispatch, getState) => {
    inboxAnalytics.ensureSentTracked(chatMessage);

    const state = getState();
    const { normalizedMessage, isOpenConversationByUser } = resolveMessageForCurrentConversation(state, chatMessage);
    if (isOpenConversationByUser) {
      dispatch(addOutgoingChatMessage(normalizedMessage));
    }

    dispatch(recordChatClientActivity({
      channel: normalizedMessage.channel,
      clientId: normalizedMessage.clientId,
      senderId: normalizedMessage.senderId,
      activityTime: dateTimeService.getCurrentTimestamp(),
    }));
  };
};
